import type { PVSizingResponse } from "../services/pvSizing";
import {
  NO_SOLUTION_WARNING_CODES,
  NOT_ESTABLISHED_WARNING_CODES,
  type PVInverterRedundancyMode,
} from "../services/pvSizingContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import {
  REDUNDANCY_LABELS as UPS_REDUNDANCY_LABELS,
  STATUS_LABELS as UPS_STATUS_LABELS,
  STATUS_TONE as UPS_STATUS_TONE,
} from "./UPSResultSummary";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";

// The same words and the same colour as the Transformer, DG and UPS summaries:
// an unestablished input is a review the engineer owes, not a design failure. No
// arrangement to build with is a failure. The maps are the UPS summary's, typed
// here against the PV enums so a value only PV gains cannot pass without words
// and a colour.
const STATUS_LABELS: Record<PVSizingResponse["status"], string> = UPS_STATUS_LABELS;

const STATUS_TONE: Record<PVSizingResponse["status"], "pass" | "fail" | "warning"> =
  UPS_STATUS_TONE;

const REDUNDANCY_LABELS: Record<PVInverterRedundancyMode, string> = UPS_REDUNDANCY_LABELS;

type NoSolutionCode = (typeof NO_SOLUTION_WARNING_CODES)[number];

// A PV study can have nothing to build with for three reasons (A19 (l)); the
// heading names the first one found, in this order: the string window decides
// whether any array exists, the MPPT limit whether it fits the inverter, and
// only then the rating schedule.
const NO_SOLUTION_HEADINGS: readonly (readonly [NoSolutionCode, string])[] = [
  ["STRING_LENGTH_WINDOW_EMPTY", "No string length fits the MPPT window"],
  ["MPPT_INPUT_LIMIT_EXCEEDED", "MPPT input limit exceeded"],
  ["NO_STANDARD_INVERTER_RATING", "No standard rating fits"],
];

/** The words at the top of the summary: the status, or the reason there is no solution. */
export function statusHeading(result: PVSizingResponse): string {
  if (result.status !== "NO_SOLUTION") {
    return STATUS_LABELS[result.status];
  }
  const codes = new Set<string>(result.warnings.map((warning) => warning.code));
  const reason = NO_SOLUTION_HEADINGS.find(([code]) => codes.has(code));
  return reason === undefined ? STATUS_LABELS.NO_SOLUTION : reason[1];
}

type PVResultSummaryProps = {
  result: PVSizingResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): sizing state, the
// inverter rating chosen from the schedule, the requirement it had to cover, the
// array and its strings, the DC/AC ratio and the AC loading.
// Display rule A12: four significant figures on screen, the exact engine
// decimal in the tooltip (title attribute). The selected rating and the target
// ratio are values the engineer supplied, so they are shown exactly as sent.
export function PVResultSummary({ result }: PVResultSummaryProps) {
  const tone = STATUS_TONE[result.status];
  const warningCount = result.warnings.length;
  const notEstablishedCount = result.warnings.filter((warning) =>
    (NOT_ESTABLISHED_WARNING_CODES as readonly string[]).includes(warning.code),
  ).length;

  const inverterRequirement = formatQuantity(result.inverter_ac_requirement_kw, "kW");
  const requiredRating = formatQuantity(result.required_unit_rating_kw, "kW");
  const installedArray = formatQuantity(result.installed_dc_array_capacity_kwp);
  const requiredArray = formatQuantity(result.required_dc_array_capacity_kwp);
  const installedRatio = formatQuantity(result.installed_dc_ac_ratio);
  const loading = formatQuantity(result.ac_loading_percent, "%");
  const noRating = result.warnings.some(
    (warning) => warning.code === "NO_STANDARD_INVERTER_RATING",
  );

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {statusHeading(result)}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} ·{" "}
          {result.jurisdiction_profile} profile
        </p>
        {noRating ? (
          <p data-no-rating="true">
            No inverter rating in the schedule covers {requiredRating.display}.
          </p>
        ) : null}
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Selected inverter rating</dt>
          {/* A catalogue value from the project design basis: shown as sent, so
              the engineer reads back the rating that is actually orderable. */}
          <dd data-summary-field="selected-rating" data-summary-value="text">
            {result.selected_unit_rating_kw === null
              ? "No inverter rating fits"
              : `${result.total_inverters} × ${result.selected_unit_rating_kw} kW`}
          </dd>
        </div>
        <div>
          <dt>Inverter AC requirement</dt>
          <dd
            data-summary-field="inverter-requirement"
            title={inverterRequirement.exact ?? undefined}
          >
            {inverterRequirement.display}
          </dd>
        </div>
        <div>
          <dt>Required inverter rating (after derating)</dt>
          <dd data-summary-field="required-rating" title={requiredRating.exact ?? undefined}>
            {requiredRating.display}
          </dd>
        </div>
        <div>
          <dt>Inverters</dt>
          {/* Counts, not engineering quantities: shown as the integers they are. */}
          <dd data-summary-field="inverters" data-summary-value="text">
            {result.duty_inverters} duty + {result.redundant_inverters} redundant ·{" "}
            {REDUNDANCY_LABELS[result.redundancy_mode]}
          </dd>
        </div>
        <div>
          <dt>PV array</dt>
          <dd
            data-summary-field="array" data-summary-value="text"
            title={
              `${installedArray.exact ?? EMPTY_QUANTITY} kWp installed · ` +
              `${requiredArray.exact} kWp required`
            }
          >
            {installedArray.display} kWp installed ({requiredArray.display} required)
          </dd>
        </div>
        <div>
          <dt>Strings</dt>
          <dd data-summary-field="strings" data-summary-value="text">
            {result.total_strings === null || result.modules_per_string === null
              ? EMPTY_QUANTITY
              : `${result.total_strings} × ${result.modules_per_string} modules ` +
                `(${result.installed_modules})`}
          </dd>
        </div>
        <div>
          <dt>DC/AC ratio</dt>
          {/* The target is the engineer's design-basis value: shown as sent. No limit
              is applied to either figure (A19 (i), GAP-016). */}
          <dd data-summary-field="dc-ac-ratio" data-summary-value="text" title={installedRatio.exact ?? undefined}>
            {installedRatio.display} installed · {result.target_dc_ac_ratio} target
          </dd>
        </div>
        <div>
          <dt>AC loading</dt>
          <dd data-summary-field="loading" title={loading.exact ?? undefined}>
            {loading.display}
          </dd>
        </div>
        {notEstablishedCount > 0 ? (
          <div>
            <dt>Inputs not established</dt>
            <dd data-summary-field="not-established">{notEstablishedCount}</dd>
          </div>
        ) : null}
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={warningCount}>{warningCount}</dd>
        </div>
      </dl>
    </section>
  );
}

export { NO_SOLUTION_HEADINGS, REDUNDANCY_LABELS, STATUS_LABELS, STATUS_TONE };
